import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { BUSINESS_UNITS } from "@/lib/businessUnits";

export function ActiveFilters({ filters, onRemoveFilter, module = "leads", appUsers = [], sdrs = [] }) {
  const activeFilters = [];
  
  // Check which filters are active
  if (filters.searchQuery) {
    activeFilters.push({
      key: 'searchQuery',
      label: `Search: ${filters.searchQuery}`,
      value: filters.searchQuery
    });
  }
  
  if (filters.assignedFilter && filters.assignedFilter !== 'all') {
    const labels = {
      'me': 'Atribuído a mim',
      'my_deals': 'Atribuído a mim',
      'my_leads': 'Atribuído a mim',
      'unassigned': 'Não atribuído',
      'team': 'Minha equipe'
    };
    
    // Check if it's a UUID (user ID)
    const isUserId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(filters.assignedFilter);
    
    activeFilters.push({
      key: 'assignedFilter',
      label: labels[filters.assignedFilter] || (isUserId ? 'Atribuído a usuário específico' : filters.assignedFilter),
      value: filters.assignedFilter
    });
  }
  
  if (filters.dateFilter && filters.dateFilter !== 'all') {
    const labels = {
      'today': 'Hoje',
      'yesterday': 'Ontem',
      'week': 'Esta semana',
      'month': 'Este mês',
      'custom': 'Data personalizada'
    };
    activeFilters.push({
      key: 'dateFilter',
      label: labels[filters.dateFilter] || filters.dateFilter,
      value: filters.dateFilter
    });
  }
  
  if (module === 'leads' && filters.kanbanColumnsFilter && filters.kanbanColumnsFilter !== 'all') {
    activeFilters.push({
      key: 'kanbanColumnsFilter',
      label: filters.kanbanColumnsFilter === 'active' ? 'Colunas Ativas' : 'Colunas Inativas',
      value: filters.kanbanColumnsFilter
    });
  }
  
  if (module === 'leads' && filters.marketFilter && filters.marketFilter !== 'all') {
    const labels = {
      'US': '🇺🇸 Mercado US',
      'BR': '🇧🇷 Mercado BR'
    };
    activeFilters.push({
      key: 'marketFilter',
      label: labels[filters.marketFilter] || filters.marketFilter,
      value: filters.marketFilter
    });
  }
  
  if (module === 'deals' && filters.kanbanColumnsFilter && filters.kanbanColumnsFilter !== 'all') {
    const labels = {
      'ativas': 'Deals Ativas',
      'fechadas': 'Deals Fechadas'
    };
    activeFilters.push({
      key: 'kanbanColumnsFilter',
      label: labels[filters.kanbanColumnsFilter] || filters.kanbanColumnsFilter,
      value: filters.kanbanColumnsFilter
    });
  }
  
  if (module === 'deals' && filters.businessUnitFilter && filters.businessUnitFilter !== 'all') {
    const labels = {
      'us': '🇺🇸 US (USD)',
      'br': '🇧🇷 BR (BRL)'
    };
    activeFilters.push({
      key: 'businessUnitFilter',
      label: `BU: ${labels[filters.businessUnitFilter] || filters.businessUnitFilter}`,
      value: filters.businessUnitFilter
    });
  }
  
  if (module === 'contacts' && filters.filterNoDeals) {
    activeFilters.push({
      key: 'filterNoDeals',
      label: 'Sem Deals',
      value: true
    });
  }

  // Onboarding module filters
  if (module === 'onboarding' && filters.statusFilter) {
    const labels = {
      'pending': 'Pendentes',
      'completed': 'Concluídos'
    };
    activeFilters.push({
      key: 'statusFilter',
      label: labels[filters.statusFilter] || filters.statusFilter,
      value: filters.statusFilter
    });
  }

  if (module === 'onboarding' && filters.myOnboardingsFilter) {
    activeFilters.push({
      key: 'myOnboardingsFilter',
      label: 'Meus Onboardings',
      value: true
    });
  }
  
  // Helper function to get user display name
  const getUserName = (userId) => {
    if (userId === 'unassigned') return 'Não atribuído';
    if (userId === 'me') return 'Atribuído a mim';
    const user = appUsers?.find(u => u.id === userId) || sdrs?.find(u => u.id === userId);
    return user?.display_name || user?.first_name || 'Desconhecido';
  };
  
  // Multi-select filter: Sales Consultant (contacts module)
  if (module === 'contacts' && Array.isArray(filters.salesConsultantFilter) && filters.salesConsultantFilter.length > 0) {
    filters.salesConsultantFilter.forEach(value => {
      activeFilters.push({
        key: 'salesConsultantFilter',
        label: `Consultor: ${getUserName(value)}`,
        value: value,
        isMulti: true
      });
    });
  } else if (module === 'contacts' && filters.salesConsultantFilter && filters.salesConsultantFilter !== 'all' && !Array.isArray(filters.salesConsultantFilter)) {
    // Legacy single value support
    activeFilters.push({
      key: 'salesConsultantFilter',
      label: `Consultor: ${getUserName(filters.salesConsultantFilter)}`,
      value: filters.salesConsultantFilter
    });
  }
  
  // Filter: Source (contacts and deals) - array support
  if ((module === 'contacts' || module === 'deals') && Array.isArray(filters.sourceFilter) && filters.sourceFilter.length > 0) {
    filters.sourceFilter.forEach(value => {
      activeFilters.push({
        key: 'sourceFilter',
        label: `Origem: ${value}`,
        value: value,
        isMulti: true
      });
    });
  } else if ((module === 'contacts' || module === 'deals') && filters.sourceFilter && filters.sourceFilter !== 'all' && !Array.isArray(filters.sourceFilter)) {
    activeFilters.push({
      key: 'sourceFilter',
      label: `Origem: ${filters.sourceFilter}`,
      value: filters.sourceFilter
    });
  }
  
  // Filter: Channel (contacts and deals) - array support
  if ((module === 'contacts' || module === 'deals') && Array.isArray(filters.channelFilter) && filters.channelFilter.length > 0) {
    filters.channelFilter.forEach(value => {
      activeFilters.push({
        key: 'channelFilter',
        label: `Canal: ${value}`,
        value: value,
        isMulti: true
      });
    });
  } else if ((module === 'contacts' || module === 'deals') && filters.channelFilter && filters.channelFilter !== 'all' && !Array.isArray(filters.channelFilter)) {
    activeFilters.push({
      key: 'channelFilter',
      label: `Canal: ${filters.channelFilter}`,
      value: filters.channelFilter
    });
  }
  
  // Filter: Closing Date Range (deals)
  if (module === 'deals' && filters.closingDateRange?.from) {
    const from = new Date(filters.closingDateRange.from);
    const to = filters.closingDateRange.to ? new Date(filters.closingDateRange.to) : null;
    const label = to 
      ? `Fechamento: ${from.toLocaleDateString('pt-BR', { month: 'short', day: 'numeric' })} - ${to.toLocaleDateString('pt-BR', { month: 'short', day: 'numeric' })}`
      : `Fechamento: ${from.toLocaleDateString('pt-BR', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    
    activeFilters.push({
      key: 'closingDateRange',
      label,
      value: filters.closingDateRange
    });
  }
  
  // Multi-select filter: SDR (contacts module)
  if (module === 'contacts' && Array.isArray(filters.sdrFilter) && filters.sdrFilter.length > 0) {
    filters.sdrFilter.forEach(value => {
      activeFilters.push({
        key: 'sdrFilter',
        label: `SDR: ${getUserName(value)}`,
        value: value,
        isMulti: true
      });
    });
  } else if (module === 'contacts' && filters.sdrFilter && filters.sdrFilter !== 'all' && !Array.isArray(filters.sdrFilter)) {
    activeFilters.push({
      key: 'sdrFilter',
      label: `SDR: ${getUserName(filters.sdrFilter)}`,
      value: filters.sdrFilter
    });
  }
  
  // Multi-select filter: Verification Status (contacts module)
  if (module === 'contacts' && Array.isArray(filters.verificationFilter) && filters.verificationFilter.length > 0) {
    const verificationLabels = {
      'unverified': 'Não verificado',
      'verified': 'Verificado',
      'invalid': 'Inválido'
    };
    filters.verificationFilter.forEach(value => {
      activeFilters.push({
        key: 'verificationFilter',
        label: `Verificação: ${verificationLabels[value] || value}`,
        value: value,
        isMulti: true
      });
    });
  } else if (module === 'contacts' && filters.verificationFilter && filters.verificationFilter !== 'all' && !Array.isArray(filters.verificationFilter)) {
    const verificationLabel = filters.verificationFilter.charAt(0).toUpperCase() + filters.verificationFilter.slice(1);
    activeFilters.push({
      key: 'verificationFilter',
      label: `Verificação: ${verificationLabel}`,
      value: filters.verificationFilter
    });
  }
  
  // Multi-select filter: Contact Type (contacts module)
  if (module === 'contacts' && Array.isArray(filters.contactTypeFilter) && filters.contactTypeFilter.length > 0) {
    const typeLabels = {
      'prospect': 'Prospect',
      'active_customer': 'Cliente Ativo',
      'past_customer': 'Ex-Cliente'
    };
    filters.contactTypeFilter.forEach(value => {
      activeFilters.push({
        key: 'contactTypeFilter',
        label: `Tipo: ${typeLabels[value] || value}`,
        value: value,
        isMulti: true
      });
    });
  } else if (module === 'contacts' && filters.contactTypeFilter && filters.contactTypeFilter !== 'all' && !Array.isArray(filters.contactTypeFilter)) {
    const typeLabels = {
      'prospect': 'Prospect',
      'active_customer': 'Cliente Ativo',
      'past_customer': 'Ex-Cliente'
    };
    activeFilters.push({
      key: 'contactTypeFilter',
      label: `Tipo: ${typeLabels[filters.contactTypeFilter] || filters.contactTypeFilter}`,
      value: filters.contactTypeFilter
    });
  }
  
  // Multi-select filter: Created By (contacts module)
  if (module === 'contacts' && Array.isArray(filters.createdByFilter) && filters.createdByFilter.length > 0) {
    filters.createdByFilter.forEach(value => {
      const label = value === 'unassigned' ? 'Desconhecido' : value === 'me' ? 'Criado por mim' : getUserName(value);
      activeFilters.push({
        key: 'createdByFilter',
        label: `Criado por: ${label}`,
        value: value,
        isMulti: true
      });
    });
  } else if (module === 'contacts' && filters.createdByFilter && filters.createdByFilter !== 'all' && !Array.isArray(filters.createdByFilter)) {
    const createdByLabel = filters.createdByFilter === 'unassigned' 
      ? 'Desconhecido' 
      : filters.createdByFilter === 'me'
      ? 'Criado por mim'
      : getUserName(filters.createdByFilter);
    activeFilters.push({
      key: 'createdByFilter',
      label: `Criado por: ${createdByLabel}`,
      value: filters.createdByFilter
    });
  }
  
  // Multi-select filter: Deal Stage (contacts module)
  if (module === 'contacts' && Array.isArray(filters.dealStageFilter) && filters.dealStageFilter.length > 0) {
    const stageLabels = {
      'no_deals': 'Sem Deals',
      'any_deal': 'Com Deal',
      'NEW': 'Novo',
      'QUALIFYING': 'Qualificação',
      'PROSPECTING': 'Prospecção',
      'PROPOSAL': 'Proposta',
      'NEGOTIATING': 'Negociação',
      'WON': 'Ganho',
      'LOST': 'Perdido'
    };
    filters.dealStageFilter.forEach(value => {
      activeFilters.push({
        key: 'dealStageFilter',
        label: `Deal: ${stageLabels[value] || value}`,
        value: value,
        isMulti: true
      });
    });
  } else if (module === 'contacts' && filters.dealStageFilter && filters.dealStageFilter !== 'all' && !Array.isArray(filters.dealStageFilter)) {
    const stageLabels = {
      'no_deals': 'Sem Deals',
      'any_deal': 'Com Deal',
      'NEW': 'Novo',
      'QUALIFYING': 'Qualificação',
      'PROSPECTING': 'Prospecção',
      'PROPOSAL': 'Proposta',
      'NEGOTIATING': 'Negociação',
      'WON': 'Ganho',
      'LOST': 'Perdido'
    };
    activeFilters.push({
      key: 'dealStageFilter',
      label: `Deal: ${stageLabels[filters.dealStageFilter] || filters.dealStageFilter}`,
      value: filters.dealStageFilter
    });
  }
  
  // Multi-select filter: Created Date (contacts module)
  if (module === 'contacts' && Array.isArray(filters.createdDateFilter) && filters.createdDateFilter.length > 0) {
    const dateLabels = {
      'today': 'Hoje',
      'yesterday': 'Ontem',
      'this_week': 'Esta Semana',
      'this_month': 'Este Mês',
      'custom': 'Período Personalizado'
    };
    filters.createdDateFilter.forEach(value => {
      activeFilters.push({
        key: 'createdDateFilter',
        label: `Criado: ${dateLabels[value] || value}`,
        value: value,
        isMulti: true
      });
    });
  } else if (module === 'contacts' && filters.createdDateFilter && filters.createdDateFilter !== 'all' && !Array.isArray(filters.createdDateFilter)) {
    const dateLabels = {
      'today': 'Hoje',
      'yesterday': 'Ontem',
      'this_week': 'Esta Semana',
      'this_month': 'Este Mês',
      'custom': 'Período Personalizado'
    };
    activeFilters.push({
      key: 'createdDateFilter',
      label: `Criado: ${dateLabels[filters.createdDateFilter] || filters.createdDateFilter}`,
      value: filters.createdDateFilter
    });
  }
  
  // Multi-select filter: Business Unit (contacts module)
  if (module === 'contacts' && Array.isArray(filters.businessUnitFilter) && filters.businessUnitFilter.length > 0) {
    filters.businessUnitFilter.forEach(value => {
      const bu = BUSINESS_UNITS.find(b => b.value === value);
      const label = value === 'unassigned' ? 'Não definido' : (bu ? `${bu.flag} ${bu.label}` : value);
      activeFilters.push({
        key: 'businessUnitFilter',
        label: `BU: ${label}`,
        value: value,
        isMulti: true
      });
    });
  }
  
  // Multi-select filter: SDR (deals module)
  if (module === 'deals' && Array.isArray(filters.sdrFilter) && filters.sdrFilter.length > 0) {
    filters.sdrFilter.forEach(value => {
      const label = value === 'unassigned' ? 'Sem SDR' : getUserName(value);
      activeFilters.push({
        key: 'sdrFilter',
        label: `SDR: ${label}`,
        value: value,
        isMulti: true
      });
    });
  }
  
  // Multi-select filter: Consultant (deals module)
  if (module === 'deals' && Array.isArray(filters.consultantFilter) && filters.consultantFilter.length > 0) {
    filters.consultantFilter.forEach(value => {
      const label = value === 'unassigned' ? 'Sem Consultor' : getUserName(value);
      activeFilters.push({
        key: 'consultantFilter',
        label: `Consultor: ${label}`,
        value: value,
        isMulti: true
      });
    });
  }
  
  // Multi-select filter: Reactivation Status (contacts module)
  if (module === 'contacts' && Array.isArray(filters.reactivationStatusFilter) && filters.reactivationStatusFilter.length > 0) {
    const statusLabels = {
      'scheduled': 'Agendado',
      'in_progress': 'Em Andamento',
      'completed': 'Concluído',
      'no_response': 'Sem Resposta'
    };
    filters.reactivationStatusFilter.forEach(value => {
      activeFilters.push({
        key: 'reactivationStatusFilter',
        label: `Reativação: ${statusLabels[value] || value}`,
        value: value,
        isMulti: true
      });
    });
  }
  
  if (activeFilters.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-2 mb-4 items-center">
      <span className="text-sm text-muted-foreground">
        Filtros ativos:
      </span>
      {activeFilters.map((filter, index) => (
        <Badge
          key={`${filter.key}-${filter.value}-${index}`}
          variant="secondary"
          className="pl-3 pr-1 py-1 flex items-center gap-1"
        >
          <span>{filter.label}</span>
          <button
            onClick={() => filter.isMulti ? onRemoveFilter(filter.key, filter.value) : onRemoveFilter(filter.key)}
            className="ml-1 hover:bg-muted rounded-full p-0.5 transition-colors"
            aria-label={`Remove filter ${filter.label}`}
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
      <button
        onClick={() => onRemoveFilter('all')}
        className="text-sm text-primary hover:underline"
      >
        Limpar todos
      </button>
    </div>
  );
}